import { Box, Button, IconBadge, Tag, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import i18n from 'modules/core/lib/i18n'

import { MaterializedStepDescription } from 'modules/processes/components/MaterializedStep/MaterializedStepDescription'
import { MaterializedStep, Step } from 'modules/processes/domain'
import useBuildStepList from 'modules/processes/hooks/steps/useBuildStepList'
import { useStepStarter } from 'modules/processes/hooks/useStepStarter'
import { useStepsMaterializationDate } from 'modules/processes/hooks/useStepsMaterializationDates'

import Items from './Items'

interface Props {
  processId: number
  steps: Step[]
  materializedProcessStatus: string
  materializedProcessId: number
  materializedSteps: MaterializedStep[]
  materializedProcessFinalizedAt?: string
  materializedProcessAccessId: number
}

const Steps = ({
  steps,
  materializedSteps,
  materializedProcessId,
  materializedProcessStatus,
  materializedProcessFinalizedAt,
  materializedProcessAccessId,
}: Props) => {
  const { sortedSteps } = useBuildStepList({
    steps,
    materializedSteps,
    materializedProcessFinalizedAt,
  })
  const { steps: stepsMaterializationDates } = useStepsMaterializationDate({
    materializedProcessId,
  })
  const { loading, stepStarter } = useStepStarter()

  const findStepMaterializationDate = (id: number) => {
    return stepsMaterializationDates.find((s) => s.stepId === id)
  }

  const stepStatus = (id: number) => {
    return materializedSteps.find((ms) => ms.stepId === id)?.status
  }

  if (loading) return <Loading />

  return (
    <Box gap="s40">
      {sortedSteps.map((step, index) => {
        const materializedStep = materializedSteps.find(
          (materializedStep) => materializedStep.id === step.materializedStepId
        )
        const isCurrentStep = materializedStep?.status === 'running'
        const isActiveStep = !!materializedStep
        const previousStepName = index === 0 ? step.name : sortedSteps[index - 1]?.name

        return (
          <Box key={step.id} gap="s16">
            <Box gap="s8" flexDirection="row" justifyContent="spaceBetween">
              <Box flexDirection="row" justifyContent="spaceBetween">
                <Box
                  flexDirection="row"
                  justifyContent="spaceBetween"
                  alignItems="flexStart"
                  gap="s8"
                >
                  <Box paddingTop="s2">
                    <IconBadge icon="list" iconColor="malibu1000" color="malibu200" />
                  </Box>

                  <Box gap="s8">
                    <Text.Header2 color={isActiveStep ? undefined : 'grey600'}>
                      {step.name}
                    </Text.Header2>
                    <MaterializedStepDescription
                      stepProgressionCondition={step.stepProgressionCondition || ''}
                      stepMaterializationDate={String(
                        findStepMaterializationDate(Number(step.id))?.materializationDate
                      )}
                      stepStatus={stepStatus(Number(step.id))}
                      materializedStepCreatedAt={materializedStep?.createdAt}
                      previousStepName={previousStepName}
                      field={step.stepProgressionConditionResourceKey}
                      stepRemoved={materializedStep && !materializedStep?.stepId}
                    />
                  </Box>
                </Box>
              </Box>
              {materializedProcessStatus !== 'finalized' && isCurrentStep && (
                <Box flexDirection="row" alignSelf="center">
                  <Tag type={Tag.Types.malibu}>
                    {i18n.t('processes.materialize_process.detail.active_step')}
                  </Tag>
                </Box>
              )}
            </Box>

            <Items
              items={(step.id ? steps.find((s) => s.id === step.id)?.items : []) || []}
              isCurrentStep={isActiveStep}
              materializedProcessId={materializedProcessId}
              materializedItems={materializedStep?.materializedItems}
              materializedProcessAccessId={materializedProcessAccessId}
            />

            <Box flexDirection="row" justifyContent="flexEnd">
              {(!materializedStep?.status || materializedStep.status === 'pending') && (
                <Button
                  onClick={() => stepStarter({ materializedProcessId, stepIds: [Number(step.id)] })}
                  type="secondary"
                >
                  {i18n.t('processes.materialize_process.detail.manually_start_step')}
                </Button>
              )}
            </Box>
          </Box>
        )
      })}
    </Box>
  )
}

export default observer(Steps)
